import React, {useState, useEffect, useRef, useMemo} from 'react';
import {Box, Input, InputGroup, InputLeftElement, Text, Badge, Flex, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper} from '@chakra-ui/react';
import Icon from '../../components/Icon';
import Fuse from 'fuse.js';

interface EventSearchProps {
  containerSelector?: string;
  venueSelector?: string;
  artistSelector?: string;
  timeSelector?: string;
}

interface ConcertItem {
  element: HTMLElement;
  artist: string;
  time: string;
}

interface EventItem {
  element: HTMLElement;
  venue: string;
  concerts: ConcertItem[];
}

const EventSearch: React.FC<EventSearchProps> = ({
  containerSelector = 'details',
  venueSelector = 'summary',
  artistSelector = 'ul li strong',
  timeSelector = 'ul li',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const [eventItems, setEventItems] = useState<EventItem[]>([]);
  const [debugMode, setDebugMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState<Array<{venue: string, score: number, matches: readonly any[]}>>([]);
  const [threshold, setThreshold] = useState<number>(0.25); // Lower threshold = more exact matching
  
  // Initialize event items on component mount
  useEffect(() => {
    const containers = document.querySelectorAll(containerSelector);
    const items: EventItem[] = [];
    
    containers.forEach((container) => {
      if (container instanceof HTMLElement) {
        const summaryElement = container.querySelector(venueSelector);
        const listElements = container.querySelectorAll('ul li');
        
        // Extract venue name
        let venueName = '';
        if (summaryElement) {
          const hasImage = summaryElement.querySelector('img') !== null;
          
          if (hasImage) {
            const venueSpan = summaryElement.querySelector('span span');
            venueName = venueSpan ? venueSpan.textContent || '' : summaryElement.textContent || '';
          } else {
            venueName = summaryElement.textContent || '';
          }
          venueName = venueName.trim();
        }
        
        // Extract concerts (artist and time pairs)
        const concerts: ConcertItem[] = [];
        listElements.forEach(listItem => {
          if (listItem instanceof HTMLElement) {
            const artistElement = listItem.querySelector('strong');
            const artistText = artistElement ? artistElement.textContent?.trim() : '';
            const timeText = listItem.textContent?.trim() || '';
            
            // Only create a concert item if we have valid data
            if (artistText && timeText) {
              concerts.push({
                element: listItem,
                artist: artistText,
                time: timeText
              });
            }
            else {
              console.log('INVALID CONCERT ITEM', {artistElement, artistText, timeText});
            }
          }
        });
        
        items.push({
          element: container,
          venue: venueName,
          concerts
        });
      }
    });
    
    setEventItems(items);
  }, []);
  
  // Create Fuse instance for fuzzy searching with 'all' filter
  const fuseOptions = useMemo(() => {
    return {
      includeScore: true,
      threshold, // Use the state variable for threshold
      ignoreLocation: true,
      keys: ['venue', 'concerts.artist', 'concerts.time'],
    };
  }, [threshold]);
  
  // Create Fuse instance
  const fuse = useMemo(() => new Fuse(eventItems, fuseOptions), [eventItems, fuseOptions]);
  
  useEffect(() => {
    if (searchTerm === '') {
      // Reset visibility of all elements when search is empty
      eventItems.forEach(item => {
        // Show the venue container
        item.element.style.display = '';
        
        // Show all concert items
        item.concerts.forEach(concert => {
          concert.element.style.display = '';
        });
      });
      
      // Clear debug info when search is empty
      setDebugInfo([]);
      return;
    }

    // Perform fuzzy search
    const term = searchTerm.trim();
    let results = fuse.search(term);
    
    // Store debug info if in debug mode
    if (debugMode) {
      const newDebugInfo = results.map(result => ({
        venue: result.item.venue,
        score: result.score || 1,
        matches: result.matches || []
      }));
      setDebugInfo(newDebugInfo);
    } else {
      setDebugInfo([]);
    }
    
    // First, hide all venue containers
    eventItems.forEach(item => {
      item.element.style.display = 'none';
    });
    
    // Check if we've matched any venues directly
    const venueMatchesMap = new Map<string, boolean>();
    
    // Create a venue-only searcher for accurate venue matching
    const venueFuse = new Fuse(eventItems, {
      includeScore: true,
      threshold, // Use the state variable for threshold
      ignoreLocation: true,
      keys: ['venue'],
      includeMatches: debugMode, // Include match details for debugging
    });
    
    const venueMatches = venueFuse.search(term);
    venueMatches.forEach(match => {
      venueMatchesMap.set(match.item.venue, true);
    });
    
    // For each result, show the venue and only matching concerts
    results.forEach(result => {
      const venueItem = result.item;
      venueItem.element.style.display = ''; // Show the venue
      
      // Check if this venue name directly matched the search term
      const isVenueMatch = venueMatchesMap.has(venueItem.venue);
      
      // By default hide all concerts in this venue
      venueItem.concerts.forEach(concert => {
        concert.element.style.display = 'none';
      });
      
      // If venue matched directly, show all concerts
      if (isVenueMatch) {
        venueItem.concerts.forEach(concert => {
          concert.element.style.display = '';
        });
      } else {
        // For artist or time filters, we need to do additional matching
        const concertFuse = new Fuse(venueItem.concerts, {
          includeScore: true,
          threshold, // Use the state variable for threshold
          ignoreLocation: true,
          keys: ['artist', 'time'],
          includeMatches: debugMode, // Include match details for debugging
        });
        
        // Find matching concerts within this venue
        let matchingConcerts = concertFuse.search(term);
        
        // Show only matching concerts
        matchingConcerts.forEach(match => {
          match.item.element.style.display = '';
        });
      }
    });
  }, [searchTerm, fuse, eventItems, debugMode, threshold]);

  const handleClear = () => {
    setSearchTerm('');
    // Blur the input to hide the keyboard
    if (searchRef.current) {
      searchRef.current.blur();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // When Enter/Return key is pressed, hide the keyboard
    if (e.key === 'Enter') {
      if (searchRef.current) {
        searchRef.current.blur();
      }
    }
  };

  // Handle debug mode toggle
  const toggleDebugMode = () => {
    setDebugMode(!debugMode);
  };

  // Handle threshold change
  const handleThresholdChange = (value: string) => {
    const newThreshold = parseFloat(value);
    if (!isNaN(newThreshold) && newThreshold >= 0 && newThreshold <= 1) {
      setThreshold(newThreshold);
    }
  };

  return (
    <Box width="100%" mb={4} mt={2} px={0}>
      <InputGroup>
        <InputLeftElement pointerEvents="none">
          <Icon iconName="fi-rr-search" style={{color: '#666', fontSize: '16px'}} />
        </InputLeftElement>
        <Input
          ref={searchRef}
          placeholder="Search by artist, venue or time..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          borderRadius="full"
          bg="white"
          border="1px solid #ddd"
          enterKeyHint="search" // Mobile keyboard will show 'search' instead of 'enter'
          _focus={{
            boxShadow: '0 0 0 1px #0066cc',
            borderColor: '#0066cc',
          }}
        />
        {searchTerm && (
          <Box
            position="absolute"
            right="10px"
            top="50%"
            transform="translateY(-50%)"
            cursor="pointer"
            zIndex={2}
            onClick={handleClear}>
            <Icon iconName="fi-rr-cross-circle" style={{color: '#999', fontSize: '16px'}} />
          </Box>
        )}
      </InputGroup>
      
      {/* Debug toggle and threshold control */}
      <Flex mt={2} justifyContent="space-between" alignItems="center">
        {/* <Text 
          as="span" 
          fontSize="sm" 
          color="gray.500" 
          cursor="pointer" 
          onClick={toggleDebugMode}
          textDecoration="underline"
        >
          {debugMode ? "Hide Debug Info" : "Show Debug Info"}
        </Text> */}
        
        {debugMode && (
          <Flex alignItems="center">
            <Text fontSize="xs" mr={2}>Fuzzy Threshold:</Text>
            <NumberInput 
              size="xs"
              maxW="80px" 
              step={0.1} 
              min={0} 
              max={1} 
              precision={1}
              value={threshold}
              onChange={handleThresholdChange}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <Text fontSize="xs" ml={2}>(Lower = stricter)</Text>
          </Flex>
        )}
      </Flex>
      
      {/* Debug information display */}
      {debugMode && debugInfo.length > 0 && (
        <Box mt={2} p={3} border="1px solid" borderColor="gray.200" borderRadius="md" bg="gray.50" fontSize="sm" overflowX="auto">
          <Text fontWeight="bold" mb={2}>Search Debug Info:</Text>
          <Text fontSize="xs" mb={2}>Note: In Fuse.js, scores closer to 0 are better matches. Scores above the threshold ({threshold}) are excluded.</Text>
          {debugInfo.map((item, idx) => (
            <Box key={idx} mb={3} pb={2} borderBottom="1px solid" borderColor="gray.200">
              <Text fontWeight="semibold">Venue: {item.venue}</Text>
              <Text color={item.score <= threshold ? "green.500" : "red.500"}>
                Score: {item.score?.toFixed(2)} {item.score <= threshold ? "(Match)" : "(Would be excluded with stricter threshold)"}
              </Text>
              {item.matches && item.matches.length > 0 && (
                <Box mt={1}>
                  <Text fontWeight="medium">Matched Fields:</Text>
                  {item.matches.map((match, midx) => {
                    // The key that matched (venue, artist, time)
                    const matchKey = match.key || '';
                    // The value that was being searched
                    const value = match.value || '';
                    // The indices where matches occurred
                    const indices = match.indices || [];
                    
                    // Create a highlighted version of the value string
                    let highlightedValue = '';
                    let lastIndex = 0;
                    
                    indices.forEach(([start, end]) => {
                      // Add text before the match
                      highlightedValue += value.substring(lastIndex, start);
                      // Add the matched text with highlighting
                      highlightedValue += `<mark style="background-color:#ffeb3b;padding:0">${value.substring(start, end + 1)}</mark>`;
                      lastIndex = end + 1;
                    });
                    
                    // Add any remaining text
                    highlightedValue += value.substring(lastIndex);
                    
                    return (
                      <Box key={midx} mt={1}>
                        <Badge colorScheme="blue" mr={1}>{matchKey}</Badge>
                        <Text as="span" dangerouslySetInnerHTML={{ __html: highlightedValue }} />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default EventSearch;
